import _set from 'lodash/set';
import _sum from 'lodash/sum';
import { BaseChartProperty } from '../../base-property';
import ChartDefaultValueFactory from '../../../../chart-models/chart-presets/default-value-factory';
import { getThemeColorValueByIndex } from '../../theme';
import { DataLabelsProperty } from '../data-label';
import type { IDataLabelsProperty } from '../data-label/data-label.interface';
import type { ISeries, ISeriesProperty } from './series.interface';
import type { NullableDeep } from '../../chart-shape-property.interface';
import type { STMarkerStyleDmlC, STTrendlineType } from '../../../../Interface/chart-v3/sdk-model.interface';
import type {
    IChartMarker,
    IChartSeries,
    IDataLabels,
} from '../../../../Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import { GeometryProperty } from '../geometry';
import { IGeometryProperty } from '../geometry/geometry.interface';
import { IChartShapeProps, ShapeProps } from '../../../../Interface/chart-v3/chart-properties.interface';
import _cloneDeep from 'lodash/cloneDeep';
import { IMarkerProperty } from '../geometry/marker/marker.interface';
import { MarkerProperty } from '../geometry/marker';

export class SeriesProperty extends BaseChartProperty<IChartSeries>
    implements ISeriesProperty {
    public serIndex: number;
    public autoThemeColor: boolean;
    private label: IDataLabelsProperty;
    private geometry: IGeometryProperty;
    private markerProperty: IMarkerProperty | undefined;

    public get seriesId() {
        return this.uiData.seriesId;
    }
    public get props(): IChartShapeProps {
        return this.uiData.props;
    }
    public get isEmptySeriesName(): boolean {
        return this.uiData.isEmptySeriesName;
    }
    public get idx() {
        return this.uiData.idx;
    }
    public get dataLabels() {
        return this.uiData.dataLabels;
    }
    public get name() {
        return this.uiData.name;
    }
    public get dataList() {
        return this.uiData.dataList;
    }

    public init(serIndex) {
        this.serIndex = serIndex;
        this.autoThemeColor = this.subChartModel.getSubChartProperty().isAutoThemeColor(this.uiData, this.idx);
        this.initDataLabelsProperty();
        this.initGeometryProperty();
        this.initMarkerProperty();
        return this;
    }

    public getDataLabelsProperty(): IDataLabelsProperty | undefined {
        return this.label;
    }

    public getGeometryProperty(showMarker: boolean): IGeometryProperty | undefined {
        return showMarker ? this.markerProperty : this.geometry;
    }

    /**
     * 删除series下的datalabel, series.dataList下datalabel也会一并删除
     * @param subChartModel
     */
    public removeDataLabels() {
        const { series } = this.subChartModel;
        const removeSeries = series.map((seriesItem) => {
            if (this.idx !== seriesItem.idx) return undefined;
            const newSeriesItem: any = {
                dataLabels: null,
            };
            if (seriesItem.dataList) {
                newSeriesItem.dataList = seriesItem.dataList.map((data) => {
                    if (data.dataLabel) {
                        return {
                            dataLabel: null,
                        };
                    }
                    return undefined;
                });
            }
            return newSeriesItem;
        });
        this.subChartModel.removeProps({
            series: removeSeries,
        });
    }

    /**
     * 更新 series 下的 datalabels， 如果没有则创建
     * 同时更新 dataList 下 dataPoint 的 dataLabel 属性
     */
    public updateDataLabels(props: Partial<IDataLabels>, updateDataList = true) {
        // 修改系列dataLabels属性
        const updateSeries: any = {
            dataLabels: this.dataLabels ? _cloneDeep(props) : ChartDefaultValueFactory.createDataLabels(props),
        };
        // 修改系列datalist下dataLabel属性
        const removeDataList = new Array(this.dataList.length).fill(undefined);
        if (updateDataList && this.dataList) {
            updateSeries.dataList = this.dataList.map((data, dataIdx) => {
                if (!data.dataLabel) return undefined;
                // 如果修改位置， 需要移除layout信息
                if (Object.keys(props).includes('labelPosition')) {
                    removeDataList[dataIdx] = {
                        dataLabel: { layout: null },
                    };
                }
                return {
                    dataLabel: props,
                };
            });
        }
        const removeSeries = {
            dataList: removeDataList,
        };
        this.updateProps(updateSeries, removeSeries);
    }

    public removeTrendlines() {
        const { series } = this.subChartModel;
        const seriesLength = series.length;
        const index = series.findIndex(seriesItem => seriesItem.idx === this.idx);
        const removeSeries = new Array(seriesLength).fill(undefined);
        removeSeries[index] = {
            trendlines: null,
        };
        this.subChartModel.removeProps({ series: removeSeries });
    }

    public createTrendline(trendlineType: STTrendlineType) {
        const { series } = this.subChartModel;
        const total = _sum(this.chartModel.plotArea.charts.map(chart => chart.series.length));
        const newSeries = series.map((ser) => {
            if (ser.idx === this.idx) {
                const props = { style: {} };
                _set(props, 'style.border.color', getThemeColorValueByIndex(this.idx!, this.chartModel.chartSpace?.colorStyle?.id as string, total));
                const trendline = ChartDefaultValueFactory.createTrendline({
                    type: trendlineType,
                    props,
                });
                const { trendlines } = ser;
                return {
                    trendlines: [...(trendlines?.map(i => undefined) || []), trendline],
                };
            }
            return undefined;
        });
        this.subChartModel.updateProps({ series: newSeries });
    }

    public getUIData() {
        return this.uiData;
    }

    /**
     * 修改 series 下图形 props ， 同时更新 dataList 下 dataPoint 的图形 props 属性
     * @param updateProps
     * @param removeProps
     */
    public updateGeometry(updateProps: Partial<ShapeProps>, removeProps?: Partial<NullableDeep<ShapeProps>>) {
        const updateSeries: any = _cloneDeep(updateProps);
        // 修改系列datalist下dataPoint属性
        const removeDataList = new Array(this.dataList.length).fill(undefined);
        if (this.dataList) {
            updateSeries.dataList = this.dataList.map((data, dataIdx) => {
                if (!data.dataPoint) return undefined;
                if (removeProps) {
                    removeDataList[dataIdx] = {
                        dataPoint: _cloneDeep(removeProps),
                    };
                }
                return {
                    dataPoint: _cloneDeep(updateProps),
                };
            });
        }
        const removeSeries = {
            ..._cloneDeep(removeProps),
            dataList: removeDataList,
        };
        this.updateProps(updateSeries, removeSeries);
    }

    public updateMarker(updateProps: Partial<IChartMarker>, removeProps?: Partial<NullableDeep<IChartMarker>>) {
        const updateSeries: any = {
            marker: _cloneDeep(updateProps),
        };
        // 修改系列datalist下dataPoint marker
        const removeDataList = new Array(this.dataList.length).fill(undefined);
        if (this.dataList) {
            updateSeries.dataList = this.dataList.map((data, dataIdx) => {
                if (!data.dataPoint?.marker) return undefined;
                if (removeProps) {
                    removeDataList[dataIdx] = {
                        dataPoint: {
                            marker: removeProps,
                        },
                    };
                }
                return {
                    dataPoint: {
                        marker: updateProps,
                    },
                };
            });
        }
        const removeSeries = {
            marker: _cloneDeep(removeProps),
            dataList: removeDataList,
        };
        this.updateProps(updateSeries, removeSeries);
    }

    public createMarker(symbol: STMarkerStyleDmlC) {
        const props = {};
        _set(props, 'style.border.color.colorValue', `accent${this.idx! + 1}`);
        this.updateMarker({
            ...ChartDefaultValueFactory.createDefaultMarker({
                props,
            }),
            symbol,
        });
    }

    public updateProps(updateProps: Partial<ISeries>, removeProps?: Partial<NullableDeep<ISeries>>) {
        const updateSeries = [...new Array(this.serIndex), updateProps];
        const removeSeries = [...new Array(this.serIndex), removeProps];
        this.subChartModel.updateAndRemoveProps(
            { series: updateSeries },
            { series: removeSeries },
        );
    }

    private get subChartModel() {
        return this.context.getSubChartModel();
    }

    private get chartModel() {
        return this.context.getChartModel();
    }

    private initDataLabelsProperty() {
        if (this.dataLabels) {
            this.label = new DataLabelsProperty(this.dataLabels, this.context).init(this);
        }
    }

    private initGeometryProperty() {
        if (this.props) {
            this.geometry = new GeometryProperty({ props: this.props }, this.context).init(this);
        }
    }

    private initMarkerProperty() {
        if (this.uiData.marker) {
            this.markerProperty = new MarkerProperty(this.uiData.marker, this.context).init(this);
        }
    }
}
